/**
 * Conversation Operator Dashboard (iter135.16) — read-only admin UI
 * surfacing three core back-pressure signals:
 *
 *   1. Notification-job pipeline (``conv_notification_jobs``) — any
 *      job still pending past its ``fire_at`` means a user is
 *      waiting on an email we haven't sent yet. Green row count +
 *      "Pending" filter gives a support engineer a single-click
 *      view.
 *   2. Rate-limit buckets (``conv_rate_limits``) — visible
 *      throttle state for OTP / invite / message-send quotas. A
 *      common support call is "user says their OTP email never
 *      arrived" — the dashboard answers "they hit the attempt cap
 *      at HH:MM" in one glance.
 *   3. Deactivated customers (``conv_deactivated_customers``) —
 *      roster of companies pulled out of the PCS by a lifecycle
 *      transition, so support can sanity-check a "why can't we
 *      message X" complaint.
 *
 * All data fetched from ``/api/admin/conv-ops/*`` — gated to super
 * admins server-side. A non-super-admin visitor sees a 403 toast and
 * is redirected back to ``/``.
 */
import { useCallback, useEffect, useState } from "react";
import { Pulse, Bell, UserMinus, ArrowsClockwise, Funnel } from "@phosphor-icons/react";
import { toast } from "sonner";
import api from "@/lib/api";
import AdminPageHeader from "@/components/AdminPageHeader";

function formatTs(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString();
}

function KpiCard({ icon: Icon, label, value, tone = "slate", testId }) {
  const toneMap = {
    slate: { bg: "#f1f5f9", fg: "#334155" },
    amber: { bg: "#fef3c7", fg: "#92400e" },
    rose:  { bg: "#ffe4e6", fg: "#9f1239" },
    emerald: { bg: "#d1fae5", fg: "#065f46" },
  }[tone];
  return (
    <div
      className="rounded-lg px-4 py-3 flex items-center gap-3 shadow-sm"
      style={{ background: toneMap.bg, color: toneMap.fg }}
      data-testid={testId}
    >
      <Icon size={24} weight="duotone" />
      <div className="flex-1">
        <div className="text-[11px] uppercase tracking-wider opacity-80">{label}</div>
        <div className="text-[22px] font-bold tabular-nums">{value}</div>
      </div>
    </div>
  );
}

export default function ConvOpsPage({ user, onLogout, onLogoutAll }) {
  const [summary, setSummary] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [rateLimits, setRateLimits] = useState([]);
  const [deactivated, setDeactivated] = useState([]);
  // iter136.33 — Recent client-side error log feed.
  const [clientErrors, setClientErrors] = useState([]);
  const [clientErrorQuery, setClientErrorQuery] = useState("");
  const [jobStatus, setJobStatus] = useState("pending");
  const [rlQuery, setRlQuery] = useState("");
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const [s, j, rl, d, ce] = await Promise.all([
        api.get("/admin/conv-ops/summary"),
        api.get(`/admin/conv-ops/notification-jobs?status=${jobStatus}&limit=200`),
        api.get(`/admin/conv-ops/rate-limits?limit=200${rlQuery ? `&q=${encodeURIComponent(rlQuery)}` : ""}`),
        api.get("/admin/conv-ops/deactivated-customers?limit=500"),
        api.get(`/operator/client-errors?limit=100${clientErrorQuery ? `&q=${encodeURIComponent(clientErrorQuery)}` : ""}`),
      ]);
      setSummary(s.data);
      setJobs(j.data?.items || []);
      setRateLimits(rl.data?.items || []);
      setDeactivated(d.data?.items || []);
      setClientErrors(ce.data?.items || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load ops data");
    } finally {
      setLoading(false);
    }
  }, [jobStatus, rlQuery, clientErrorQuery]);

  useEffect(() => { refresh(); }, [refresh]);

  return (
    <div className="min-h-screen" style={{ background: "#f8fafc" }}>
      <AdminPageHeader
        title="Conversation Operations"
        subtitle="Read-only triage for notification jobs, rate limits, and deactivated customers"
        icon={<Pulse size={20} weight="duotone" className="text-slate-700" />}
        user={user}
        onLogout={onLogout}
        onLogoutAll={onLogoutAll}
      />

      <div className="max-w-[1400px] mx-auto px-3 sm:px-6 py-6 space-y-6">
        {/* KPI strip */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3" data-testid="conv-ops-kpis">
          <KpiCard
            icon={Bell}
            label="Pending notification jobs"
            value={summary?.pending_jobs ?? "—"}
            tone={summary?.pending_jobs > 0 ? "amber" : "slate"}
            testId="conv-ops-kpi-pending-jobs"
          />
          <KpiCard
            icon={UserMinus}
            label="Deactivated customers"
            value={summary?.deactivated_customers ?? "—"}
            tone={summary?.deactivated_customers > 0 ? "rose" : "slate"}
            testId="conv-ops-kpi-deactivated"
          />
          <KpiCard
            icon={Funnel}
            label="Active rate-limit buckets (10 min)"
            value={summary?.active_rate_limit_buckets ?? "—"}
            tone="slate"
            testId="conv-ops-kpi-active-rl"
          />
        </div>

        <div className="flex justify-end">
          <button
            onClick={refresh}
            disabled={loading}
            data-testid="conv-ops-refresh-btn"
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-slate-900 text-white text-[12px] font-semibold hover:bg-slate-700 disabled:opacity-60"
          >
            <ArrowsClockwise size={14} weight="bold" className={loading ? "animate-spin" : ""} />
            Refresh
          </button>
        </div>

        {/* Notification jobs */}
        <section
          className="bg-white rounded-lg shadow-sm border border-slate-200"
          data-testid="conv-ops-notification-jobs"
        >
          <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200">
            <div className="flex items-center gap-2">
              <Bell size={18} weight="duotone" className="text-slate-600" />
              <h2 className="font-bold text-slate-800">Notification jobs</h2>
              <span className="text-[11px] text-slate-500">{jobs.length} row(s)</span>
            </div>
            <div className="flex items-center gap-1">
              {["pending", "delivered", "cancelled", "all"].map((s) => (
                <button
                  key={s}
                  onClick={() => setJobStatus(s)}
                  data-testid={`conv-ops-jobs-filter-${s}`}
                  className={`px-2.5 py-1 text-[11px] font-semibold rounded-full uppercase tracking-wider border ${
                    jobStatus === s
                      ? "bg-slate-900 text-white border-slate-900"
                      : "bg-white text-slate-600 border-slate-300 hover:bg-slate-50"
                  }`}
                >
                  {s}
                </button>
              ))}
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-[12px]">
              <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500">
                <tr>
                  <th className="px-3 py-2 text-left">Job</th>
                  <th className="px-3 py-2 text-left">User</th>
                  <th className="px-3 py-2 text-left">Conversation</th>
                  <th className="px-3 py-2 text-left">Created</th>
                  <th className="px-3 py-2 text-left">Fire at</th>
                  <th className="px-3 py-2 text-left">Delivered</th>
                  <th className="px-3 py-2 text-left">Cancelled</th>
                  <th className="px-3 py-2 text-left">Reason</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {jobs.length === 0 && (
                  <tr><td colSpan={8} className="px-3 py-6 text-center text-slate-400">No jobs.</td></tr>
                )}
                {jobs.map((j) => (
                  <tr key={j.job_id} className="hover:bg-slate-50" data-testid={`conv-ops-job-${j.job_id}`}>
                    <td className="px-3 py-2 font-mono text-[10px] text-slate-500">{j.job_id.slice(0, 10)}…</td>
                    <td className="px-3 py-2">{j.user_type}:{j.user_id}</td>
                    <td className="px-3 py-2 font-mono text-[10px] text-slate-500">{j.conversation_id.slice(0, 10)}…</td>
                    <td className="px-3 py-2 text-slate-600">{formatTs(j.created_at)}</td>
                    <td className="px-3 py-2 text-slate-600">{formatTs(j.fire_at)}</td>
                    <td className="px-3 py-2 text-emerald-700">{formatTs(j.delivered_at)}</td>
                    <td className="px-3 py-2 text-rose-700">{formatTs(j.cancelled_at)}</td>
                    <td className="px-3 py-2 text-slate-500">{j.reason || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        {/* Rate limits */}
        <section
          className="bg-white rounded-lg shadow-sm border border-slate-200"
          data-testid="conv-ops-rate-limits"
        >
          <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200">
            <div className="flex items-center gap-2">
              <Funnel size={18} weight="duotone" className="text-slate-600" />
              <h2 className="font-bold text-slate-800">Rate-limit buckets</h2>
              <span className="text-[11px] text-slate-500">{rateLimits.length} row(s)</span>
            </div>
            <input
              type="text"
              placeholder="Filter by key substring…"
              value={rlQuery}
              onChange={(e) => setRlQuery(e.target.value)}
              data-testid="conv-ops-rl-filter"
              className="text-[12px] border border-slate-300 rounded-md px-2 py-1 w-60"
            />
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-[12px]">
              <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500">
                <tr>
                  <th className="px-3 py-2 text-left">Key</th>
                  <th className="px-3 py-2 text-left">Window start</th>
                  <th className="px-3 py-2 text-right">Count</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {rateLimits.length === 0 && (
                  <tr><td colSpan={3} className="px-3 py-6 text-center text-slate-400">No active buckets.</td></tr>
                )}
                {rateLimits.map((r, i) => (
                  <tr key={`${r.rl_key}-${r.window_start}-${i}`} className="hover:bg-slate-50">
                    <td className="px-3 py-2 font-mono text-[11px]">{r.rl_key}</td>
                    <td className="px-3 py-2 text-slate-600">{formatTs(r.window_start)}</td>
                    <td className="px-3 py-2 text-right tabular-nums font-semibold">{r.count}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        {/* Deactivated customers */}
        <section
          className="bg-white rounded-lg shadow-sm border border-slate-200"
          data-testid="conv-ops-deactivated"
        >
          <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200">
            <div className="flex items-center gap-2">
              <UserMinus size={18} weight="duotone" className="text-slate-600" />
              <h2 className="font-bold text-slate-800">Deactivated customers</h2>
              <span className="text-[11px] text-slate-500">{deactivated.length} row(s)</span>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-[12px]">
              <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500">
                <tr>
                  <th className="px-3 py-2 text-left">Customer</th>
                  <th className="px-3 py-2 text-left">Deactivated at</th>
                  <th className="px-3 py-2 text-left">By</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {deactivated.length === 0 && (
                  <tr><td colSpan={3} className="px-3 py-6 text-center text-slate-400">None deactivated.</td></tr>
                )}
                {deactivated.map((d) => (
                  <tr key={d.customer_id} className="hover:bg-slate-50">
                    <td className="px-3 py-2 font-mono">{d.customer_id}</td>
                    <td className="px-3 py-2 text-slate-600">{formatTs(d.deactivated_at)}</td>
                    <td className="px-3 py-2 text-slate-500">
                      {d.deactivated_by_user_type ? `${d.deactivated_by_user_type}:${d.deactivated_by_user_id}` : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        {/* iter136.33 — Recent client-side errors. Auto-fed by every
            ``extractErr()`` call on the frontend. Helps support spot
            broken flows in production without users having to report. */}
        <section className="bg-white border border-slate-200 rounded-lg" data-testid="conv-ops-client-errors">
          <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between gap-3 flex-wrap">
            <div>
              <h2 className="text-sm font-bold text-slate-900">Recent client errors</h2>
              <p className="text-[11px] text-slate-500 mt-0.5">Last 100 entries · auto-purged after 14 days · validation/auth errors filtered</p>
            </div>
            <input
              data-testid="client-errors-search"
              value={clientErrorQuery}
              onChange={(e) => setClientErrorQuery(e.target.value)}
              placeholder="Filter by message / URL / page…"
              className="text-[12px] border border-slate-300 rounded px-2 py-1 w-64"
            />
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-[12px]">
              <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500">
                <tr>
                  <th className="px-3 py-2 text-left whitespace-nowrap">When</th>
                  <th className="px-3 py-2 text-left">User</th>
                  <th className="px-3 py-2 text-left">Where</th>
                  <th className="px-3 py-2 text-center">Status</th>
                  <th className="px-3 py-2 text-left">Message</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {clientErrors.length === 0 && (
                  <tr><td colSpan={5} className="px-3 py-6 text-center text-slate-400">No recent client errors. 🎉</td></tr>
                )}
                {clientErrors.map((e, idx) => (
                  <tr key={idx} className="hover:bg-slate-50" data-testid={`client-error-row-${idx}`}>
                    <td className="px-3 py-2 text-slate-500 whitespace-nowrap">{formatTs(e.created_at)}</td>
                    <td className="px-3 py-2 font-mono text-slate-700">{e.user_email || <span className="text-slate-300">guest</span>}</td>
                    <td className="px-3 py-2 text-slate-600">
                      <div className="font-mono text-[11px] truncate max-w-[260px]" title={`${e.method || ""} ${e.url || ""}`}>{e.method || "?"} {(e.url || "").replace(/^https?:\/\/[^/]+/, "")}</div>
                      <div className="text-[10px] text-slate-400 truncate max-w-[260px]" title={e.page || ""}>page: {e.page || "—"}</div>
                    </td>
                    <td className="px-3 py-2 text-center">
                      <span className={`inline-block px-1.5 py-0.5 rounded text-[10px] font-bold ${
                        e.status >= 500 ? "bg-red-100 text-red-700"
                        : e.status >= 400 ? "bg-amber-100 text-amber-700"
                        : "bg-slate-100 text-slate-600"
                      }`}>{e.status ?? "—"}</span>
                    </td>
                    <td className="px-3 py-2 text-slate-700">
                      <div className="line-clamp-2" title={e.message}>{e.message}</div>
                      {e.code && <div className="text-[10px] text-slate-400 mt-0.5">code: <span className="font-mono">{e.code}</span></div>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      </div>
    </div>
  );
}
